'use client';

import { captureException } from '@sentry/nextjs';
import { toDirectedGraph } from '@xstate/graph';
import { assign } from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { ComponentProps, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';

import {
  onCheckPhoneNumberTaken,
  onResetPassword,
  onSendVerificationToken,
  onSubmitEmail,
} from '@/authentication/templates/AuthFormLegacy/AuthForm.helpers';
import { AuthFormErrors } from '@/authentication/templates/AuthFormLegacy/AuthForm.types';
import { handleCarmaClientErrorMessage } from '@/data/Carma/helpers/handleCarmaClientErrorMessage';
import { confirmQuote, getQuoteRequest, requestQuote } from '@/data/FinanceApi/FinanceApi';
import { financeQuoteRequestPayloadToPersonalizedQuotePayloadPartial } from '@/data/FinanceApi/financeQuoteRequestPayloadToPersonalizedQuotePayloadPartial';
import { personalizedQuotePayloadPartialToFinanceQuoteRequestPayload } from '@/data/FinanceApi/personalizedQuotePayloadPartialToFinanceQuoteRequestPayload';
import { ChatStatus } from '@/general/components/Chat/ChatStatus';
import { FullPageLoading } from '@/general/components/FullPageLoading/FullPageLoading';
import { ToasterService } from '@/general/components/Toaster/ToasterService';
import { flattenQueryParam } from '@/general/helpers/flattenQueryParam';
import { useRecaptcha } from '@/general/helpers/useRecaptcha';
import { PersonalisedQuote } from '@/personalised-quote/PersonalisedQuote';
import { StateOrderMappings, getPersonalisedQuoteProgress } from '@/personalised-quote/PersonalisedQuote.helpers';
import {
  MACHINE_STATE,
  PersonalisedQuoteMachineInitialState,
  personalisedQuoteMachine,
} from '@/personalised-quote/PersonalisedQuote.machine';
import { PersonalisedQuotePayloadPartial } from '@/personalised-quote/templates/Forms/PersonalizedQuoteForm.type';
import { default as AuthService, default as authService } from '@/services/AuthService';
import { getPLPCarData } from '@/services/CarService';
import { authStore, getUserAuthDataFromCookies } from '@/stores/authStore';
import { useCarmaError } from '@/stores/errorStore';
import { parseFilterParams } from '@/utils/filterHelper';
import { formatPhoneForPayload } from '@/utils/formUtils';
import { getOr } from '@/utils/getOr';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

export const PersonalisedQuoteForm: FC = () => {
  const getErrorMessage = useCarmaError();
  const { isLoadingRecaptcha, getRecaptchaToken } = useRecaptcha();
  const router = useRouter();
  const searchParams = useSearchParams();
  const purchasePrice = flattenQueryParam(searchParams.get('purchasePrice') ?? undefined);
  const depositAmount = flattenQueryParam(searchParams.get('depositAmount') ?? undefined);
  const originPath = flattenQueryParam(searchParams.get('origin') ?? undefined);
  const requestId = flattenQueryParam(searchParams.get('quoteRequestId') ?? undefined);

  const initialValues: PersonalisedQuotePayloadPartial =
    purchasePrice && depositAmount
      ? {
          loanDetails: {
            estimatedPurchasePrice: Number(purchasePrice),
            depositAmount: Number(depositAmount),
            preferredBuildYear: '',
          },
        }
      : {};

  const { userData, userAuthData, isUserLoggedIn, setUserAuthData, setUserData } = authStore(
    (state) => ({
      userData: state.userData,
      isUserLoggedIn: state.isUserLoggedIn,
      setUserAuthData: state.setUserAuthData,
      setUserData: state.setUserData,
      userAuthData: state.userAuthData,
    }),
    shallow,
  );

  // When user switches back to tab after resetting password.
  // Login state is required on render as it determines whether certain questions are shown
  const handleVisibility = () => {
    if (!document.hidden && !isUserLoggedIn) {
      try {
        // Load auth
        const userAuthData = getUserAuthDataFromCookies();
        setUserAuthData(userAuthData);
      } catch (error) {
        // If an error happens loading the profile, assumes the user is logged-out

        console.error(error);
        router.push('/api/auth/signout');
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (userAuthData) {
        const result = await AuthService.getProfile();
        setUserData(result);
      }
    })();
  }, [userAuthData]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [handleVisibility]);

  const onSignin = async (email: string, password: string) => {
    try {
      const result = await authService.signIn({ username: email, password });
      setUserAuthData({ id: result.customer_id, accessToken: result.access_token });
      handleVisibility();
    } catch (error) {
      return handleApiErrorMessage(error, 'Failed to login, please try again.');
    }

    return true;
  };

  const [initialPayload, setInitialPayload] = useState<PersonalisedQuotePayloadPartial>(initialValues);
  const [isPersonalisedQuoteReady, setIsPersonalisedQuoteReady] = useState<boolean>(false);

  const onCloseForm = () => {
    pushToDataLayer({ event: 'exit_personalized_quote' });
    router.push(originPath || '/');
  };

  const onClickCarmaLogo = () => {
    pushToDataLayer({ event: 'exit_personalized_quote' });
    // Use globalThis.location.href for full page navigation to avoid Next.js router issues
    globalThis.location.href = '/';
  };

  const stateOrderMappings: StateOrderMappings = useMemo(
    () =>
      toDirectedGraph(personalisedQuoteMachine)
        .children.map((val) => ({
          [val.stateNode.key]: val.stateNode.order,
        }))
        .reduce(assign, {}),
    [],
  );

  const fetchCarsCount = useCallback((buildYear: string, purchasePrice: string) => {
    // Define the async function that returns a promise
    const fetchData = async () => {
      try {
        const carResponse = await getPLPCarData({
          filters: parseFilterParams({
            year: { min: buildYear, max: '' },
            price: {
              min: '',
              max: purchasePrice,
              priceType: 'cash',
            },
          }),
          sort: 'recommended',
        });
        return getOr(carResponse, 'total', 0);
      } catch (error) {
        console.log(error);
      }
      return 0;
    };

    // Call the async function and return the promise
    return fetchData();
  }, []);

  const initialMachineState: PersonalisedQuoteMachineInitialState = useMemo(
    () => ({
      ...personalisedQuoteMachine.initialState,
      ...{
        value: getPersonalisedQuoteProgress(
          initialPayload,
          isUserLoggedIn,
          !!userData,
          MACHINE_STATE.START,
          stateOrderMappings,
        ).maxStep,
        context: {
          payload: initialPayload,
          isBackTransition: false,
          isLoggedIn: isUserLoggedIn,
          isExistingAccount: false,
        },
      },
    }),
    [initialPayload],
  );

  useEffect(() => {
    const getDataQuote = async () => {
      if (requestId) {
        try {
          const quoteData = await getQuoteRequest(requestId);
          if (quoteData) {
            const quotePayloadPartial = financeQuoteRequestPayloadToPersonalizedQuotePayloadPartial(
              quoteData,
              initialValues,
            );
            quotePayloadPartial.financeQuoteRequest = {
              quoteRequestId: requestId,
            };
            setInitialPayload(quotePayloadPartial);
          }
        } catch (error) {
          console.error('Error get data quote', error);
        }
      }
      setIsPersonalisedQuoteReady(true);
    };
    getDataQuote();
  }, [requestId]);

  const onUpdatePersonalisedQuote: ComponentProps<typeof PersonalisedQuote>['onUpdatePersonalisedQuote'] = useCallback(
    async (currentState, payload) => {
      // Send the finance quote info
      if (
        (currentState === MACHINE_STATE.TERMS_AND_CONDITIONS && isUserLoggedIn) ||
        currentState === MACHINE_STATE.VERIFY_ACCOUNT
      ) {
        try {
          const requestPayload = personalizedQuotePayloadPartialToFinanceQuoteRequestPayload(payload);
          const quoteResult = await requestQuote(requestPayload);
          payload.financeQuoteRequest = {
            quoteRequestId: quoteResult.id,
          };
        } catch (error) {
          console.error(error);
          const errorMessage = handleCarmaClientErrorMessage(error);
          ToasterService.error({ message: errorMessage });
          return errorMessage;
        }
      }
      return payload;
    },
    [userData],
  );

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media only screen and (max-width: 445px) {
              iframe#launcher {
                bottom: 100px !important;
              }
            }
          `,
        }}
      />
      <ChatStatus visibility="hidden" />
      <>
        {requestId && !isPersonalisedQuoteReady ? (
          <FullPageLoading />
        ) : (
          <PersonalisedQuote
            userData={userData}
            initialMachineState={initialMachineState}
            onUpdatePersonalisedQuote={onUpdatePersonalisedQuote}
            onConfirmFinanceQuote={confirmQuote}
            onCloseQuote={onCloseForm}
            onClickCarmaLogo={onClickCarmaLogo}
            fetchCarsCount={fetchCarsCount}
            isLoggedIn={isUserLoggedIn}
            onSubmitEmail={onSubmitEmail}
            onSignin={onSignin}
            onResetPassword={onResetPassword}
            onCheckPhoneNumberTaken={onCheckPhoneNumberTaken}
            onSendVerificationToken={onSendVerificationToken}
            onSignup={async ({
              email,
              firstName,
              lastName,
              phone,
              password,
              marketing,
              verificationId,
              verificationCode,
            }) => {
              let result: string | boolean = true;
              const formattedPhone = formatPhoneForPayload(phone);

              if (isLoadingRecaptcha) {
                return 'Security check is still loading. Please wait a moment and try again.';
              }

              let recaptchaToken: string = '';
              try {
                recaptchaToken = await getRecaptchaToken('auth');
              } catch (error) {
                console.error('Error getting recaptcha token', error);
                captureException(error);
              }

              try {
                const signUpPayload = {
                  phone: formattedPhone,
                  email,
                  firstName,
                  lastName,
                  marketing,
                  password,
                  verification: {
                    verification_code: verificationCode,
                    verification_id: verificationId,
                  },
                  recaptchaToken,
                };

                await authService.signUp(signUpPayload);
              } catch (error: any) {
                const { error_key: key, error_message: message } = getOr(error, 'response.data', {});
                const errorMessage = getErrorMessage(key, message, 'Failed to sign-up, please try again later.');
                return errorMessage;
              }
              try {
                result = await onSignin(email, password);
              } catch (error) {
                console.error('Error signing in', error);
                return 'Unknown error happened';
              }
              return result;
            }}
          />
        )}
      </>
    </>
  );
};

const handleApiErrorMessage = (error: unknown, defaultErrorMessage: string): string => {
  const errorCode: number = getOr(error as any, 'response.data.error_code', 0);
  switch (errorCode) {
    case AuthFormErrors.PENDING_VERIFICATION:
      return 'Invalid verification code provided.';
    case AuthFormErrors.ACCOUNT_NOT_VALIDATED:
      return 'The account is not verified, please contact customer support for assistance.';
    case AuthFormErrors.PASSWORD_INVALID:
      return 'Password incorrect, please try again.';
    default:
      return defaultErrorMessage;
  }
};
